import { Input, InputHandle, InputProps } from "@/ui";
import { useTranslations } from "@services/strings";
import {
  ChangeEventHandler,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRtl } from "@hooks/useRtl";

export type DecimalInputProps = Omit<InputProps, "onChange" | "value"> & {
  formatNumber?: (n: number) => string;
  onChange: (value: number) => void;
  parseNumber?: (s: string) => number;
  value: number;
};

/**
 * A text input that keeps its value formatted with thousands separators
 * while the caret stays where the user expects.
 */
export const DecimalInput = (props: DecimalInputProps) => {
  const rtl = useRtl();
  const element = useRef<HTMLInputElement>();

  const { currencySymbol, formatNumber, parseNumber } = useTranslations();

  const [value, setValue] = useState<number>(props.value);
  const [range, setRange] = useState<[number, number]>([0, 0]);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  useEffect(() => {
    const [start, end] = range;
    if (!element.current || (start <= 0 && end <= 0)) {
      return;
    }

    element.current.setSelectionRange(range[0], range[1]);
  }, [range]);

  // TODO Generalize to allow for swapping periods and commas as decimal separators
  const commas = useCallback(
    (n: number): number =>
      (props.formatNumber ?? formatNumber)(n).match(/,/g)?.length ?? 0,
    [formatNumber, props.formatNumber],
  );

  const getNumericValue = (e: React.ChangeEvent<HTMLInputElement>): number => {
    const eventValue = e.target.value.replace(currencySymbol, "");
    const input = (props.parseNumber ?? parseNumber)(eventValue);
    return isNaN(input) ? 0 : input;
  };

  const formatOnChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const { selectionStart, selectionEnd } = e.target;
    const next = getNumericValue(e);

    const prevCommas = commas(value);
    const eventCommas = commas(next);
    const offset = eventCommas - prevCommas;

    props.onChange(next);
    setValue(next);

    if (!selectionStart || !selectionEnd) {
      return;
    }

    setRange([selectionStart + offset, selectionEnd + offset]);
  };

  // Strip the number helpers so they don't land on the DOM element.
  const { formatNumber: _format, parseNumber: _parse, ...inputProps } = props; // eslint-disable-line @typescript-eslint/no-unused-vars

  return (
    <Input
      {...inputProps}
      type="text"
      inputMode="numeric"
      dir={rtl ? "rtl" : "ltr"}
      value={(props.formatNumber ?? formatNumber)(value)}
      onChange={formatOnChange}
      ref={element as Ref<InputHandle>}
    />
  );
};
